import React, { useEffect, useState } from 'react';
import { api, fileUrl } from '@/lib/api';
import { TextField, Toggle, Section, Row, SaveBar } from '@/admin/AdminUI';
import ImageUploader from '@/admin/ImageUploader';

const DEFAULTS = {
  enabled: true,
  name: 'USA Today Sports Media Group',
  logo: 'https://customer-assets.emergentagent.com/job_seicon-build/artifacts/94ggowrg_1.png',
  alt: 'USA Today Sports Media Group',
  url: 'https://sports.usatoday.com',
  hero_eyebrow_label: 'PRESENTED BY',
  partners_eyebrow_label: 'PRESENTING SPONSOR',
  show_in_hero: true,
  show_in_footer: true,
  show_in_partners: true,
};

export default function PresentingSponsorTab() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');

  const load = async () => {
    try {
      const r = await api.get('/admin/settings/presenting-sponsor');
      // Merge server data on top of defaults so booleans missing from legacy
      // docs still render correctly in the UI.
      setData({ ...DEFAULTS, ...(r.data || {}) });
    } catch (e) {
      setData({ ...DEFAULTS });
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (!data) return <div className="font-body text-ink-muted">Loading…</div>;

  const set = (k, v) => setData({ ...data, [k]: v });

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setSaveError('');
    try {
      await api.put('/admin/settings/presenting-sponsor', data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail) && detail[0]?.msg
        ? detail[0].msg
        : (typeof detail === 'string' ? detail : (e?.message || 'Save failed'));
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  const resetLogoToDefault = () => set('logo', DEFAULTS.logo);

  return (
    <div className="max-w-3xl" data-testid="admin-presenting-sponsor-tab">
      <Section title="Presenting Sponsor — Master Toggle">
        <p className="font-body text-[12px] text-ink-muted mb-2">
          The presenting sponsor (e.g. <b>USA Today Sports Media Group</b>) is surfaced in
          three places on the homepage: the Hero (below the date line), the Footer
          (above the orange CTA headline), and the Partners block (as a distinct
          card above the partners marquee). Use the master toggle below to disable
          all three placements at once, or use the per-placement toggles in the
          <i> Visibility &amp; Placement </i> section further down.
        </p>
        <Toggle
          label="Presenting Sponsor enabled (master switch)"
          value={!!data.enabled}
          onChange={(v) => set('enabled', v)}
          testid="ps-enabled"
        />
      </Section>

      <Section title="Sponsor Identity">
        <Row>
          <TextField
            label="Display Name"
            value={data.name || ''}
            onChange={(v) => set('name', v)}
            placeholder="USA Today Sports Media Group"
            testid="ps-name"
          />
          <TextField
            label="Alt Text (accessibility)"
            value={data.alt || ''}
            onChange={(v) => set('alt', v)}
            placeholder="USA Today Sports Media Group"
            testid="ps-alt"
          />
        </Row>
        <TextField
          label="Click-through URL"
          value={data.url || ''}
          onChange={(v) => set('url', v)}
          placeholder="https://sports.usatoday.com"
          testid="ps-url"
        />
        <p className="font-body text-[11px] text-ink-soft mt-1">
          Clicking the logo opens this URL in a new tab. Leave blank to render the logo
          as a non-clickable image. Must start with <code>http://</code> or <code>https://</code>.
        </p>
      </Section>

      <Section title="Logo">
        <ImageUploader
          value={data.logo || ''}
          onChange={(v) => set('logo', v)}
          label="Sponsor Logo"
          testid="ps-logo-upload"
        />
        <div className="flex items-start gap-3 mt-3">
          <button
            type="button"
            onClick={resetLogoToDefault}
            className="btn-secondary !py-2 !px-3 text-[11px]"
            data-testid="ps-logo-reset"
          >
            Reset to USA Today Sports default
          </button>
        </div>
        <p className="font-body text-[11px] text-ink-soft mt-2 max-w-xl leading-relaxed">
          Recommended: PNG with transparent background. The logo is rendered against
          dark and light backgrounds across the three placements, so a transparent
          PNG works best. Default is the USA Today Sports stacked lockup.
        </p>
      </Section>

      <Section title="Eyebrow Labels">
        <p className="font-body text-[12px] text-ink-muted mb-2">
          The small uppercase label that sits above the logo. The Hero and Footer
          placements share the first label (typically <i>“PRESENTED BY”</i>), while
          the Partners placement uses its own (typically <i>“PRESENTING SPONSOR”</i>).
        </p>
        <Row>
          <TextField
            label="Hero + Footer Eyebrow"
            value={data.hero_eyebrow_label || ''}
            onChange={(v) => set('hero_eyebrow_label', v)}
            placeholder="PRESENTED BY"
            testid="ps-hero-eyebrow"
          />
          <TextField
            label="Partners Eyebrow"
            value={data.partners_eyebrow_label || ''}
            onChange={(v) => set('partners_eyebrow_label', v)}
            placeholder="PRESENTING SPONSOR"
            testid="ps-partners-eyebrow"
          />
        </Row>
      </Section>

      <Section title="Visibility & Placement">
        <p className="font-body text-[12px] text-ink-muted mb-2">
          Show or hide the presenting sponsor in each placement independently. The
          master switch above overrides all three.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Toggle
            label="Show in Hero (below date line)"
            value={data.show_in_hero !== false}
            onChange={(v) => set('show_in_hero', v)}
            testid="ps-show-hero"
          />
          <Toggle
            label="Show in Footer (above CTA headline)"
            value={data.show_in_footer !== false}
            onChange={(v) => set('show_in_footer', v)}
            testid="ps-show-footer"
          />
          <Toggle
            label="Show in Partners (above marquee)"
            value={data.show_in_partners !== false}
            onChange={(v) => set('show_in_partners', v)}
            testid="ps-show-partners"
          />
        </div>
      </Section>

      <Section title="Live Preview">
        <p className="font-body text-[12px] text-ink-muted mb-3">
          Approximation of how the sponsor block renders. Final appearance varies
          slightly by placement (background color, sizing).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <PreviewCard
            label="Hero / Footer Preview (dark bg)"
            background="#0A0A0C"
            textClass="text-white/70"
            eyebrow={data.hero_eyebrow_label || 'PRESENTED BY'}
            logo={data.logo}
            alt={data.alt}
          />
          <PreviewCard
            label="Partners Preview (light bg)"
            background="#F4EFE7"
            textClass="text-ink-soft"
            eyebrow={data.partners_eyebrow_label || 'PRESENTING SPONSOR'}
            logo={data.logo}
            alt={data.alt}
          />
        </div>
      </Section>

      {saveError && (
        <div
          className="mb-3 p-3 border border-brand-red/40 bg-brand-red/5 rounded-sm font-body text-[12px] text-brand-red"
          data-testid="ps-save-error"
        >
          <b>Save error:</b> {saveError}
        </div>
      )}

      <SaveBar onSave={save} saving={saving} saved={saved} />
    </div>
  );
}

function PreviewCard({ label, background, textClass, eyebrow, logo, alt }) {
  return (
    <div className="border border-border-line rounded-sm overflow-hidden">
      <div className="px-3 py-2 bg-bg-canvas font-body text-[10px] uppercase text-ink-soft tracking-wider2 border-b border-border-line">
        {label}
      </div>
      <div className="p-6 flex flex-col items-center justify-center gap-2" style={{ background }}>
        <p className={`font-body font-semibold text-[10px] uppercase tracking-wider3 ${textClass}`}>
          {eyebrow}
        </p>
        {logo ? (
          <img
            src={fileUrl(logo)}
            alt={alt || 'Presenting sponsor'}
            className="h-10 w-auto object-contain"
          />
        ) : (
          <span className={`font-body text-[12px] italic ${textClass}`}>No logo set</span>
        )}
      </div>
    </div>
  );
}
